import { translateServerError } from "@/lib/serverErrors";
import { privateClient } from "@/api/privateClient";
import { INSTITUTION_ADMIN_ENDPOINTS } from "../../institutionAdmin.endpoints";
import type { SyllabusEntry, SyllabusTopic, MasterSyllabus, MasterSyllabusScope, SyllabusPublishScope } from "../types";

export async function fetchMySyllabus(institutionId: number, staffAssignmentId: number): Promise<SyllabusEntry> {
  const { data } = await privateClient.get(INSTITUTION_ADMIN_ENDPOINTS.SYLLABUS.MINE(institutionId, staffAssignmentId));
  return data;
}

export async function submitSyllabus(institutionId: number, staffAssignmentId: number, topics: SyllabusTopic[]): Promise<SyllabusEntry> {
  const { data } = await privateClient.put(INSTITUTION_ADMIN_ENDPOINTS.SYLLABUS.MINE(institutionId, staffAssignmentId), { topics });
  return data;
}

export async function fetchMasterSyllabus(institutionId: number, scope: MasterSyllabusScope): Promise<MasterSyllabus> {
  const { data } = await privateClient.get(INSTITUTION_ADMIN_ENDPOINTS.SYLLABUS.MASTER(institutionId), { params: scope });
  return data.master;
}

export async function publishMasterSyllabus(institutionId: number, scope: SyllabusPublishScope): Promise<MasterSyllabus> {
  const { data } = await privateClient.post(INSTITUTION_ADMIN_ENDPOINTS.SYLLABUS.PUBLISH(institutionId), scope);
  return data.master;
}

export async function unpublishMasterSyllabus(institutionId: number, scope: SyllabusPublishScope): Promise<MasterSyllabus> {
  const { data } = await privateClient.post(INSTITUTION_ADMIN_ENDPOINTS.SYLLABUS.UNPUBLISH(institutionId), scope);
  return data.master;
}

export type SyllabusPrintTemplate = "GRID" | "LIST" | "COMPACT";

export async function fetchSyllabusPrintSettings(institutionId: number): Promise<{ template: SyllabusPrintTemplate }> {
  const { data } = await privateClient.get(INSTITUTION_ADMIN_ENDPOINTS.SYLLABUS.PRINT_SETTINGS(institutionId));
  return data.settings;
}

export async function saveSyllabusPrintSettings(institutionId: number, template: SyllabusPrintTemplate): Promise<{ template: SyllabusPrintTemplate }> {
  const { data } = await privateClient.put(INSTITUTION_ADMIN_ENDPOINTS.SYLLABUS.PRINT_SETTINGS(institutionId), { template });
  return data.settings;
}

export interface SubjectTeacher {
  staffAssignmentId: number;
  staffId: number;
  staffName: string;
  hasEntry: boolean;
  topicCount: number;
}

/** Admin-only: which teacher(s) hold a class+subject's SUBJECT_TEACHER assignment, to pick whose syllabus entry to edit. */
export async function fetchSubjectTeachers(institutionId: number, scope: SyllabusPublishScope): Promise<SubjectTeacher[]> {
  const { data } = await privateClient.get(INSTITUTION_ADMIN_ENDPOINTS.SYLLABUS.SUBJECT_TEACHERS(institutionId), { params: scope });
  return data.teachers;
}

export function errorMessage(err: unknown, fallback: string): string {
  return translateServerError(rawErrorMessage(err, fallback));
}

function rawErrorMessage(err: unknown, fallback: string): string {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const e = err as any;
  return e?.response?.data?.error || e?.message || fallback;
}
